import { useElementBounding, useWindowSize } from '@vueuse/core';
import { ref, computed, watch, onBeforeUnmount, type Ref } from 'vue';
import eventBus from '@ve/services/eventBus';

interface AnchorObserver {
    observe: (container: HTMLElement) => void;
    disconnect: () => void;
}

interface AnchorObserverContext {
    el: HTMLElement;
    leadBannerAnchor?: AnchorObserver | null;
}

export function createAnchorObserver(channel: string, selector: string, context: AnchorObserverContext): AnchorObserver {
    let observer: IntersectionObserver | null = null;

    context.leadBannerAnchor?.disconnect();

    const instance: AnchorObserver = {
        observe(container: HTMLElement) {
            this.disconnect();
            const target = container.querySelector<HTMLElement>(selector);
            if (!target) return;

            observer = new IntersectionObserver((entries) => {
                const entry = entries[0];
                eventBus.$emit(`portal-anchor:${channel}`, entry.target, !!entry?.isIntersecting);
            });
            observer.observe(target);
        },
        disconnect() {
            observer?.disconnect();
            observer = null;
        },
    };

    instance.observe(context.el);

    return instance;
}

interface PortalAnchorReturn {
    anchorPosition: Ref<{ top: string; left: string } | null>;
    resetAnchor: () => void;
    anchorEl: Readonly<Ref<HTMLElement | null>>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function usePortalAnchor(
    channel: string,
    floatingComponentRef: Ref<any>,
    shouldResetIfNotVisible?: boolean,
): PortalAnchorReturn {
    const anchorEl = ref<HTMLElement | null>(null);

    const floatingEl = computed(() => floatingComponentRef.value?.$el as HTMLElement | undefined);
    const { height: floatingHeight } = useElementBounding(floatingEl);
    const { width: widthSize } = useWindowSize();
    
    const refreshTrigger = ref(0);

    const anchorPosition = computed(() => {
        if (refreshTrigger.value >= 0 && anchorEl.value && widthSize.value) {
            return {
                top: `${anchorEl.value.offsetTop}px`,
                left: `${anchorEl.value.offsetLeft}px`,
                width: `${anchorEl.value.offsetWidth}px`,
            }
        }
        return null;
    })

    const eventName = `portal-anchor:${channel}`;
    const refreshEventName = `${eventName}:refresh`;

    const onAnchorVisible = async (el: HTMLElement, isVisible: boolean) => {
        if (isVisible) {
            anchorEl.value = el;
        }
        if (!isVisible && shouldResetIfNotVisible) {
            anchorEl.value = null;
        }
    };

    eventBus.$on(eventName, onAnchorVisible);

    const refreshAnchor = () => {
        refreshTrigger.value += 1;
    };

    eventBus.$on(refreshEventName, refreshAnchor);

    const resetAnchor = () => {
        anchorEl.value = null;
    };

    watch([floatingHeight, anchorEl], ([height, anchor]) => {
        if (height && anchor) {
            anchor.style.height = `${Math.round(height)}px`;
        }
    });

    onBeforeUnmount(() => {
        eventBus.$off(eventName, onAnchorVisible);
        eventBus.$off(refreshEventName, refreshAnchor);
    });

    return {
        anchorPosition,
        resetAnchor,
        anchorEl: anchorEl as Readonly<Ref<HTMLElement | null>>,
    };
}
